import { DecimalPipe } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import { Decimal } from 'decimal.js';
import { ToastrService } from 'ngx-toastr';
import { AttachmentModalData, AttachmentModalEntityData } from '../../Attachment/attachment.modal.model';
import { CommentModalData, CommentsService } from '../../Comments/comments.service';
import { SDHttpService } from '../../Common/Routing/sd-http.service';
import { AnnualDetailsService } from '../annual-details.service';
import { AnnualDetailYear } from '../Annual-Year/annual-year.model';
import { AnnualAssessmentDetail, Assessment, EqualizationFactorIntent } from './assessment.model';

import { map, sortBy } from 'lodash/fp';
import * as _ from 'lodash';
import { AssessmentComponentTypeEnum } from '../../constants.new';
import CalcTvUsingTypeEnum = Weissman.Model.CalcTvUsingTypeEnum;
import {
    MessageBoxButtons,
    MessageBoxResult,
    MessageBoxService,
} from '../../UI-Lib/Message-Box/messagebox.service.upgrade';
import { matchTaxableValueIds, SavedTaxableValue } from './taxableValueIdMatching';

const ASSESSMENT_ENTITY_TYPE_ID = 7;

@Injectable()
export class AssessmentService {
    constructor(
        private http: SDHttpService,
        private annualDetailsService: AnnualDetailsService,
        public toastr: ToastrService,
        public messageBox: MessageBoxService,
    ) {}

    getAssessment(assessmentID: number): Promise<Assessment> {
        return this.http.get(`/api/annualassessment/${assessmentID}`);
    }

    async getAssessmentViewModelByID(
        assessmentID: number,
        assessorID: number,
        parcelID: number,
        parcelAcctNum: string,
        annualYear: AnnualDetailYear,
    ): Promise<AssessmentViewModel> {
        const assessment = assessmentID
            ? await this.getAssessment(assessmentID)
            : new Assessment(annualYear.annualYearID, 0, 0, 'add', []);
        const viewModel = this.buildAssessmentViewModel(assessment);

        viewModel.assessorID = assessorID;
        viewModel.parcelID = parcelID;
        viewModel.parcelAcctNum = parcelAcctNum;
        viewModel.currentYear = annualYear;

        return viewModel;
    }

    getAssessmentViewModelByAssessmentID(assessmentID: number): Promise<AssessmentViewModel> {
        return this.http
            .get(`api/annualassessmentview/assessment/${assessmentID}`)
            .then((result: AssessmentViewDTO) => {
                const vm = this.buildAssessmentViewModel(result.assessment);
                vm.assessorID = result.assessorID;
                vm.components = result.componentTypes;
                vm.currentYear = result.annualYearView;
                return vm;
            });
    }

    getAssessmentComponent(params): Promise<AnnualAssessmentDetail> {
        return this.http.get('/api/AnnualAssessmentDetail', {
            params: params,
        });
    }

    saveAssessmentFromViewModel(
        viewModel: AssessmentViewModel,
        // No longer used to gate saving the year: the backend persists AnnualYear changes (including
        // AutoCalculateTaxableValue) unconditionally, so this flag only ever controlled a previous-year
        // recalculation cascade that this call site never triggers (it always passes false to saveYear).
        // Kept for call signature compatibility with existing callers.
        _recalculateAssessmentDetails: boolean,
        allowAddYearPrompt: boolean = false,
    ): Promise<Assessment> {
        const prepareAssessment = (): Assessment => {
            const assessment = this.prepareAssessmentForSave(
                viewModel.model,
                viewModel.preEditModelBackup,
                viewModel.taxableValueIdsBySequence,
            );
            assessment.allowAddYearPrompt = allowAddYearPrompt;

            return assessment;
        };

        // Every save can replace the saved taxable value rows with new ones, so the baseline their ids are matched
        // against is refreshed from what the server returns.
        const saveAssessment = (): Promise<Assessment> =>
            this.saveAssessment(prepareAssessment()).then((result) => {
                if (result?.taxableValues) {
                    viewModel.setPersistedTaxableValues(result.taxableValues);
                }

                return result;
            });

        //if the year's autocalculate has been set to false, it wasn't saved yet, so we must save it
        if (viewModel.dirtyAnnualYear || viewModel.dirtyTargetValue) {
            return this.annualDetailsService.saveYear(viewModel.currentYear, false).then((result) => {
                viewModel.currentYear.rowVersion = result.rowVersion;
                viewModel.model.annualYearRowVersion = result.rowVersion;

                this.showUnmappedTaxAuthoritiesWarning(result.unmappedTaxAuthorityNames);

                const resultModel = _.find(result.annualGridDetails, (detailModel) => {
                    return detailModel.annualAssessmentID === viewModel.model.annualAssessmentID;
                });

                if (resultModel) {
                    viewModel.model.rowVersion = resultModel.rowVersion;
                }

                // NOTE: It may look like we want "viewModel.dirtyAnnualYear = false;" here, but the dirty flag
                // should tell the grid to reload the year later on, so we don't want to reset that flag yet
                viewModel.dirtyAnnualYear = false;
                viewModel.dirtyTargetValue = false;
                return saveAssessment();
            });
        }

        return saveAssessment();
    }

    deleteAssessment(assessmentID: number): Promise<any> {
        return this.http.delete(`/api/AnnualAssessment/${assessmentID}`);
    }

    cancelAssessmentEdit(viewModel: AssessmentViewModel): void {
        viewModel.cancelEdit();
    }

    prepareAssessmentForSave(
        annualAssessment: Assessment,
        editBackup?: Assessment,
        taxableValueIdsBySequence?: Map<number, number>,
    ): Assessment {
        const annualAssessmentCopy = _.cloneDeep(annualAssessment);

        if (annualAssessmentCopy.taxableValuesEdited) {
            const removedRows = annualAssessmentCopy.deletedTaxableValues ?? [];

            // The removed rows are still in the edit backup, so their ids are passed in to keep the remaining rows
            // from being given one of them back; a row must never be sent as both kept and removed.
            annualAssessmentCopy.taxableValues = this.restoreTaxableValueIds(
                annualAssessmentCopy.taxableValues,
                editBackup?.taxableValues,
                taxableValueIdsBySequence,
                new Set(removedRows.map((row) => row.annualAssessmentTaxableValueId)),
            );

            annualAssessmentCopy.taxableValues = [
                ...(annualAssessmentCopy.taxableValues ?? []),
                ...removedRows.map((row) => ({ ...row, isDeleted: true })),
            ];
        }

        delete annualAssessmentCopy.deletedTaxableValues;

        annualAssessmentCopy.annualAssessmentDetails = annualAssessmentCopy.annualAssessmentDetails.map((comp) => {
            if (!comp.fmvOverridden) {
                comp.fairMarketValue = null;
            } else if (comp.assessmentComponentTypeID === AssessmentComponentTypeEnum.Adjustment) {
                comp.fairMarketValue = -Math.abs(comp.fairMarketValue);
                comp.assessedValue = -Math.abs(comp.assessedValue);
            }

            comp.ratio = comp.ratio === comp.assessmentClassRatio.assessmentRatio ? null : comp.ratio;
            return comp;
        });

        if (this.areDeadlinesEqual(annualAssessmentCopy)) {
            annualAssessmentCopy.appealDeadline = null;
        }

        return annualAssessmentCopy;
    }

    private restoreTaxableValueIds(
        taxableValues: Core.AnnualAssessmentTaxableValue[],
        originalTaxableValues: Core.AnnualAssessmentTaxableValue[],
        taxableValueIdsBySequence: Map<number, number>,
        removedTaxableValueIds: Set<number>,
    ): Core.AnnualAssessmentTaxableValue[] {
        if (!taxableValues?.length || (!originalTaxableValues?.length && !taxableValueIdsBySequence?.size)) {
            return taxableValues;
        }

        const originalRows: SavedTaxableValue[] = (originalTaxableValues ?? []).filter(
            (row) => row.annualAssessmentTaxableValueId > 0,
        );
        const originalIds = new Set(originalRows.map((row) => row.annualAssessmentTaxableValueId));
        // The ids by sequence can include saved rows the edit backup no longer has, which can only be told apart by
        // their sequence.
        const savedRows: SavedTaxableValue[] = [...originalRows];
        taxableValueIdsBySequence?.forEach((id, sequence) => {
            if (!originalIds.has(id)) {
                savedRows.push({ annualAssessmentTaxableValueId: id, sequence });
            }
        });

        const usedIds = new Set([
            ...taxableValues
                .filter((row) => row.annualAssessmentTaxableValueId > 0)
                .map((row) => row.annualAssessmentTaxableValueId),
            ...removedTaxableValueIds,
        ]);
        const rowsWithoutId = taxableValues.filter(
            (row) =>
                !(row.annualAssessmentTaxableValueId > 0) &&
                !(row as Core.AnnualAssessmentTaxableValue & { isNew?: boolean }).isNew,
        );
        const matchedIds = matchTaxableValueIds(
            rowsWithoutId,
            savedRows.filter((row) => !usedIds.has(row.annualAssessmentTaxableValueId)),
        );

        return taxableValues.map((row) => {
            const matchedId = matchedIds[rowsWithoutId.indexOf(row)];

            return matchedId > 0 ? { ...row, annualAssessmentTaxableValueId: matchedId } : row;
        });
    }

    areDeadlinesEqual(assessment: Assessment): boolean {
        const appeal = new Date(assessment.appealDeadline);
        const original = new Date(assessment.originalDeadline);

        if (appeal && original) {
            return (
                appeal.getFullYear() === original.getFullYear() &&
                appeal.getMonth() === original.getMonth() &&
                appeal.getDate() === original.getDate()
            );
        } else {
            return !appeal && !original;
        }
    }

    saveAssessment(assessment: Assessment): Promise<Assessment> {
        if (assessment.hasNegativeTaxable) {
            this.toastr.warning('Taxable values less than zero will be shown as zero.');
        }

        return this.http.put('/api/annualassessment/', assessment);
    }

    toggleAssessmentEdit(viewModel: AssessmentViewModel, editMode: boolean): void {
        if (editMode) {
            viewModel.beginEdit();
        }
    }

    prepareAnnualAssessmentForCreate(annualAssessment: Assessment): Assessment {
        const annualAssessmentCopy: Assessment = _.cloneDeep(annualAssessment);

        annualAssessmentCopy.annualAssessmentDetails = annualAssessmentCopy.annualAssessmentDetails
            .filter((item) => {
                return item.efAction !== 'delete';
            })
            .map((comp) => {
                if (!comp.fmvOverridden) {
                    comp.fairMarketValue = null;
                }

                comp.ratio = comp.ratio === comp.assessmentClassRatio.assessmentRatio ? null : comp.ratio;
                return comp;
            });

        if (this.areDeadlinesEqual(annualAssessmentCopy)) {
            annualAssessmentCopy.appealDeadline = null;
        }

        return annualAssessmentCopy;
    }

    //This function is basically the opposite of prepareAssessmentForSave
    prepareAnnualAssessment(annualAssessment: Assessment): Assessment {
        annualAssessment.annualAssessmentDetails = _.flow([
            map(function (comp) {
                //Prepare Ratio
                if (comp.ratio === null) {
                    comp.ratio = comp.assessmentClassRatio.assessmentRatio;
                }

                //Prepare FMV
                if (comp.fairMarketValue !== null) {
                    comp.fmvOverridden = true;
                } else {
                    comp.fairMarketValue = new Decimal(comp.assessedValue).dividedBy(comp.ratio).round().toNumber();
                }
                return comp;
            }),
            sortBy('sequence'),
        ])(annualAssessment.annualAssessmentDetails);

        //Prepare appeal deadline
        if (annualAssessment.appealDeadline === null) {
            annualAssessment.appealDeadline = annualAssessment.originalDeadline;
        }

        return annualAssessment;
    }

    getUniqueValues(values: string[]): string[] {
        const uniqueValues: string[] = [];

        for (const value of values ?? []) {
            if (!uniqueValues.includes(value)) {
                uniqueValues.push(value);
            }
        }
        return uniqueValues;
    }

    private buildAssessmentViewModel(result: Assessment) {
        const viewModel = new AssessmentViewModel(this);
        viewModel.model = this.prepareAnnualAssessment(result);
        viewModel.setPersistedTaxableValues(result.taxableValues);

        return viewModel;
    }

    private showUnmappedTaxAuthoritiesWarning(unmappedTaxAuthorityNames: string[]): void {
        if (!unmappedTaxAuthorityNames?.length) {
            return;
        }

        this.toastr.warning(
            `The following Tax Authorities could not be automatically mapped to a Taxable Value and were left unmapped: ${unmappedTaxAuthorityNames.join(', ')}`,
            'Manual Taxable Value Conversion',
        );
    }
}

export class AssessmentViewModel {
    constructor(private assessmentService: AssessmentService) {}

    model: Assessment;
    taxableValueIdsBySequence = new Map<number, number>();
    // The taxable value rows as the server last returned them. The rows shown while editing are recalculated on the
    // screen, so this is what tells which saved row, and which id, each of them is. Every save can replace the saved
    // rows with new ids, so it is refreshed from each save response. A restored data entry draft is not server output,
    // so it never replaces it.
    persistedTaxableValues: Core.AnnualAssessmentTaxableValue[] = [];
    isLastAssessment: boolean;
    assessorID: number;
    parcelID: number;
    parcelAcctNum: string;
    components: any[];
    currentYear: AnnualDetailYear;
    // TODO: Consider refactoring this; when originally built, auto-calculate was the only thing from AnnualYear
    // being updated in assessment. Now there's target value, and more may come later. We're handling those fields
    // separately, but we could unify that. For now, "dirtyAnnualYear" means that autoCalc has changed.
    dirtyAnnualYear: boolean;
    dirtyTargetValue: boolean;
    commentModalData: CommentModalData;
    attachmentModalData: AttachmentModalData;
    hasWritePermission: boolean;
    AssessmentChanged: (model: Assessment) => void;
    // Raised once a saved assessment has replaced the model, so the assessment panel can rebuild the copy of the
    // taxable values it displays (a save can change them, e.g. a switch to Prior Year Setup).
    SavedAssessmentAssigned: (model: Assessment) => void;
    // Raised when the user declines the Auto Calculate TV confirmation, so the owner of the edit session can
    // clear the dirty state if the switch was the only thing that had been changed.
    AutoSwitchDeclined: () => void;
    // Raised when the user declines the Calc TV Using confirmation, for the same reason.
    CalcTvUsingDeclined: () => void;

    preEditModelBackup: Assessment;
    // The targetValue and Auto Calculate TV are properties on the year, so they need to get backed up separately
    private preEditTargetValueBackup: number;
    private preEditAutoCalculateTaxableValueBackup: boolean;
    private preEditCalcTvUsingBackup: CalcTvUsingTypeEnum;
    // The taxable values as they were staged right before Auto Calculate TV was checked, so declining the confirmation
    // can bring back just those instead of discarding the whole edit session.
    private taxableValuesBeforeAutoSwitch: Pick<
        Assessment,
        'taxableValues' | 'deletedTaxableValues' | 'taxableValuesEdited'
    >;
    // The taxable values as they were staged right before Calc TV Using was first changed, so choosing the original value
    // again, or declining the confirmation, can bring back just those.
    private taxableValuesBeforeCalcTvUsingChange: Pick<
        Assessment,
        'taxableValues' | 'deletedTaxableValues' | 'taxableValuesEdited'
    >;

    // The Calc TV Using the Annual Year had when the edit session began.
    get storedCalcTvUsing(): CalcTvUsingTypeEnum {
        return this.preEditCalcTvUsingBackup ?? this.currentYear?.calcTVUsing;
    }

    get hasCalcTvUsingChanged(): boolean {
        return (
            this.preEditCalcTvUsingBackup !== undefined &&
            this.currentYear?.calcTVUsing !== this.preEditCalcTvUsingBackup
        );
    }

    beginEdit(): void {
        if (this.model) {
            this.preEditModelBackup = _.cloneDeep(this.model) as Assessment;
            this.preEditTargetValueBackup = this.currentYear.targetValue;
            this.preEditAutoCalculateTaxableValueBackup = this.currentYear.autoCalculateTaxableValue;
            this.preEditCalcTvUsingBackup = this.currentYear.calcTVUsing;
        } else {
            this.preEditModelBackup = undefined;
            this.preEditTargetValueBackup = undefined;
            this.preEditAutoCalculateTaxableValueBackup = undefined;
            this.preEditCalcTvUsingBackup = undefined;
        }

        this.taxableValuesBeforeAutoSwitch = undefined;
        this.taxableValuesBeforeCalcTvUsingChange = undefined;
        this.CalcTvUsingDeclined = undefined;
    }

    rememberTaxableValuesBeforeAutoSwitch(): void {
        this.taxableValuesBeforeAutoSwitch = _.cloneDeep({
            taxableValues: this.model.taxableValues,
            deletedTaxableValues: this.model.deletedTaxableValues,
            taxableValuesEdited: this.model.taxableValuesEdited,
        });
    }

    rememberTaxableValuesBeforeCalcTvUsingChange(): void {
        if (this.taxableValuesBeforeCalcTvUsingChange) {
            return;
        }

        this.taxableValuesBeforeCalcTvUsingChange = _.cloneDeep({
            taxableValues: this.model.taxableValues,
            deletedTaxableValues: this.model.deletedTaxableValues,
            taxableValuesEdited: this.model.taxableValuesEdited,
        });
    }

    // Brings back the taxable values staged before Calc TV Using was changed; false when nothing was remembered.
    restoreTaxableValuesBeforeCalcTvUsingChange(): boolean {
        if (!this.taxableValuesBeforeCalcTvUsingChange) {
            return false;
        }

        const { taxableValues, deletedTaxableValues, taxableValuesEdited } = _.cloneDeep(
            this.taxableValuesBeforeCalcTvUsingChange,
        );
        this.model.taxableValues = taxableValues;
        this.model.deletedTaxableValues = deletedTaxableValues;
        this.model.taxableValuesEdited = taxableValuesEdited;
        this.taxableValuesBeforeCalcTvUsingChange = undefined;

        return true;
    }

    // Puts the year's Calc TV Using back to what it was when the edit session began.
    restoreCalcTvUsing(): void {
        if (this.preEditCalcTvUsingBackup !== undefined) {
            this.currentYear.calcTVUsing = this.preEditCalcTvUsingBackup;
        }
    }

    //used to re-set the backup in cases like autosave when we need to re-set
    // the backup outside of starting edit
    setEditBackup(): void {
        _.assign(this.preEditModelBackup, this.model);
    }

    cancelEdit(): void {
        //if the year has changed, aka if autocalc has changed, change it back
        //we only save the year if we change TO calc, because this generates the numbers
        //this is done poorly but we are restricted by how auto calculate works
        //so this is basically "if changed and true, make it false and save"
        if (this.dirtyAnnualYear && (!this.currentYear.calcProjected || !this.currentYear.calcCapProjected)) {
            if (!this.currentYear.calcProjected) {
                this.currentYear.calcProjected = true;
            }

            if (!this.currentYear.calcCapProjected) {
                this.currentYear.calcCapProjected = true;
            }
            //this.annualDetailsService.saveYear(this.currentYear);

            this.dirtyAnnualYear = false;
            this.dirtyTargetValue = false;
        }
        this.currentYear.targetValue = this.preEditTargetValueBackup;
        // A cancelled Auto Calculate TV change must not linger on the year, or it would become the baseline of the next edit.
        if (this.preEditAutoCalculateTaxableValueBackup !== undefined) {
            this.currentYear.autoCalculateTaxableValue = this.preEditAutoCalculateTaxableValueBackup;
        }
        this.restoreCalcTvUsing();
        this.taxableValuesBeforeCalcTvUsingChange = undefined;
        this.model = structuredClone(this.preEditModelBackup);
        if (this.AssessmentChanged) {
            this.AssessmentChanged(this.model);
        }
    }

    validateEqualizationFactor(callback: (isValid: boolean, message: string) => void): void {
        if (this.model.equalizationFactorIntent !== EqualizationFactorIntent.Set) {
            callback(true, '');
            return;
        }

        const factor = this.model.equalizationFactor;
        const isValid =
            factor !== null &&
            factor !== undefined &&
            factor >= 0.0001 &&
            factor <= 10 &&
            new Decimal(factor).decimalPlaces() <= 4;

        callback(
            isValid,
            isValid ? '' : 'Eq. Factor must be between 0.0001 and 10.0000 with no more than four decimal places.',
        );
    }

    completeEqualizationFactorSave(): void {
        this.model.equalizationFactorIntent = EqualizationFactorIntent.None;
    }

    // Blank (both assessed and market value left empty) is a valid, savable state for any row -
    // new or existing - it collapses to 0/0 on save and is a legitimate way to represent a Taxable
    // Value not yet mapped to a Tax Authority Line Item. Only a *mismatched* pair (one amount
    // provided, the other left blank) is rejected here, since the server can't compute a meaningful
    // tax amount from a partial row. Also blocks Save when two rows share the same (non-blank) name,
    // so users can reliably tell rows apart.
    // Shown via a toastr (matching the pattern used elsewhere, e.g. Compliance's save validation)
    // rather than the Save button's tooltip, so the callback message is left blank - passing a
    // message would also trigger the tooltip.
    validateTaxableValues(callback: (isValid: boolean, message: string) => void): void {
        const rows = this.model.taxableValues || [];

        const isBlank = (value: number): boolean => value === null || value === undefined;

        const mismatchedRow = rows.find((row) => isBlank(row.assessedValue) !== isBlank(row.marketValue));

        if (mismatchedRow) {
            this.assessmentService.toastr.error(
                'Each Taxable Value must have both an Assessed Value and a Market Value, or leave both blank.',
            );
            callback(false, '');
            return;
        }

        const seenDescriptions = new Set<string>();
        const hasDuplicateDescription = rows.some((row) => {
            const description = row.description?.trim().toLowerCase();
            if (!description) {
                return false;
            }
            if (seenDescriptions.has(description)) {
                return true;
            }
            seenDescriptions.add(description);
            return false;
        });

        if (hasDuplicateDescription) {
            this.assessmentService.toastr.error('Taxable Value names must be unique.');
        }

        callback(!hasDuplicateDescription, '');
    }

    // Reverting a Manual Taxable Value mode Annual Year to Auto Calculate TV deletes the manual structure, values, and
    // Tax Authority mappings of every revision on Save, so the user must confirm first. Declining aborts the Save
    // without a message (so no Save tooltip is shown) and leaves the staged edits intact.
    async confirmManualToAutoSwitch(callback: (isValid: boolean, message: string) => void): Promise<void> {
        const isSwitchingToAuto =
            this.currentYear?.autoCalculateTaxableValue === true &&
            this.preEditAutoCalculateTaxableValueBackup === false;

        if (!isSwitchingToAuto) {
            callback(true, '');
            return;
        }

        try {
            const result = await this.assessmentService.messageBox.open({
                title: 'WARNING',
                message:
                    this.currentYear.calcTVUsing === CalcTvUsingTypeEnum.PriorYearSetup
                        ? 'Enabling Auto Calculate TV will replace the manual taxable value structure, values and Tax Authority mappings ' +
                          'of this Tax Year with the setup carried forward from the prior Tax Year. Finalized revisions keep their current ' +
                          'values, but earlier revisions mappings will no longer be available and the Bill Cluster will show only the ' +
                          'system-calculated taxes. Do you wish to continue?'
                        : 'Enabling Auto Calculate TV will replace the manual taxable value structure and values and delete the Tax Authority ' +
                          'mappings for all revisions of this Tax Year, but earlier revisions mappings will no longer be available and the ' +
                          'Bill Cluster will show only the system-calculated taxes. Do you wish to continue?',
                buttons: MessageBoxButtons.YesNo,
            });

            if (result !== MessageBoxResult.Yes) {
                this.revertAutoSwitch();
            }

            callback(result === MessageBoxResult.Yes, '');
        } catch {
            // Without a confirmation the Save must not proceed, and the user needs to know why nothing was saved.
            this.assessmentService.toastr.error(
                'Unable to confirm enabling Auto Calculate TV. The changes were not saved.',
            );
            callback(false, '');
        }
    }

    // Changing Calc TV Using on a year that stays Auto Calculate TV replaces its taxable values on Save (Prior Year Setup:
    // rows, values and Tax Authority mappings from the prior Tax Year; System Logic: the mappings are deleted and the values
    // recalculated), so the user must confirm first. Switching to Auto Calculate TV has its own confirmation, which already
    // covers the chosen Calc TV Using. Declining aborts the Save without a message and undoes only this change.
    async confirmCalcTvUsingChange(callback: (isValid: boolean, message: string) => void): Promise<void> {
        const isSwitchingToAuto = this.preEditAutoCalculateTaxableValueBackup === false;

        if (!this.currentYear?.autoCalculateTaxableValue || isSwitchingToAuto || !this.hasCalcTvUsingChanged) {
            callback(true, '');
            return;
        }

        try {
            const result = await this.assessmentService.messageBox.open({
                title: 'WARNING',
                message:
                    this.currentYear.calcTVUsing === CalcTvUsingTypeEnum.PriorYearSetup
                        ? 'Changing Calc TV Using to Prior Year Setup will replace the taxable values and Tax Authority mappings ' +
                          'of this Tax Year with the setup carried forward from the prior Tax Year. Finalized revisions keep their ' +
                          'current values. Do you wish to continue?'
                        : 'Changing Calc TV Using to System Logic will delete the Tax Authority mappings for all revisions of this ' +
                          'Tax Year and recalculate its taxable values. Finalized revisions keep their current values. Do you wish ' +
                          'to continue?',
                buttons: MessageBoxButtons.YesNo,
            });

            if (result !== MessageBoxResult.Yes) {
                this.revertCalcTvUsingChange();
            }

            callback(result === MessageBoxResult.Yes, '');
        } catch {
            // Without a confirmation the Save must not proceed, and the user needs to know why nothing was saved.
            this.assessmentService.toastr.error(
                'Unable to confirm changing Calc TV Using. The changes were not saved.',
            );
            callback(false, '');
        }
    }

    private revertCalcTvUsingChange(): void {
        this.restoreCalcTvUsing();
        this.restoreTaxableValuesBeforeCalcTvUsingChange();

        const calcTvUsingDeclined = this.CalcTvUsingDeclined;
        this.CalcTvUsingDeclined = undefined;
        calcTvUsingDeclined?.();

        if (this.AssessmentChanged) {
            this.AssessmentChanged(this.model);
        }
    }

    // Declining the confirmation only undoes the Auto Calculate TV change and the taxable values it recalculated; every
    // other staged edit stays in the edit session.
    private revertAutoSwitch(): void {
        this.currentYear.autoCalculateTaxableValue = this.preEditAutoCalculateTaxableValueBackup;
        // A Calc TV Using chosen while switching is part of the switch that was declined.
        this.restoreCalcTvUsing();
        this.taxableValuesBeforeCalcTvUsingChange = undefined;

        if (this.taxableValuesBeforeAutoSwitch) {
            const { taxableValues, deletedTaxableValues, taxableValuesEdited } = _.cloneDeep(
                this.taxableValuesBeforeAutoSwitch,
            );
            this.model.taxableValues = taxableValues;
            this.model.deletedTaxableValues = deletedTaxableValues;
            this.model.taxableValuesEdited = taxableValuesEdited;
            this.taxableValuesBeforeAutoSwitch = undefined;
        }

        const autoSwitchDeclined = this.AutoSwitchDeclined;
        this.AutoSwitchDeclined = undefined;
        autoSwitchDeclined?.();

        if (this.AssessmentChanged) {
            this.AssessmentChanged(this.model);
        }
    }

    attachmentIcon(hasAttachments): string {
        return hasAttachments ? 'fa-files-o' : 'fa-paperclip';
    }

    assignFromExistingAssessment(assessment: Assessment): void {
        if (!this.model) {
            throw new Error('Invalid attempt to load assessment from existing data when no current assessment is set');
        }

        if (this.model.annualAssessmentID !== assessment.annualAssessmentID) {
            throw new Error(
                `Invalid attempt to map assessment with assessmentID ${
                    assessment.annualAssessmentID
                } into assessment with assessmentID ${this.model.annualAssessmentID}`,
            );
        }

        _.assign(this.model, this.assessmentService.prepareAnnualAssessment(assessment));
        this.model.deletedTaxableValues = [];
        this.SavedAssessmentAssigned?.(this.model);
    }

    setPersistedTaxableValues(taxableValues: Core.AnnualAssessmentTaxableValue[]): void {
        this.persistedTaxableValues = _.cloneDeep(
            (taxableValues ?? []).filter((row) => row.annualAssessmentTaxableValueId > 0),
        );
        this.taxableValueIdsBySequence = new Map(
            this.persistedTaxableValues.map((row) => [row.sequence, row.annualAssessmentTaxableValueId]),
        );
    }

    isActual(): boolean {
        return this.model.status === 1;
    }

    updateModalData(): void {
        const assessment = this.model;
        //let appealLevel = this.appealLevelAbbrDisplay(appeal.appealLevelID);
        const description = `${+this.currentYear.annualYear1} - ${assessment.revisionDesc}`;

        // comment data
        this.commentModalData = new CommentModalData();
        this.commentModalData.entityID = assessment.annualAssessmentID;
        this.commentModalData.entityTypeID = ASSESSMENT_ENTITY_TYPE_ID;
        this.commentModalData.canEdit = this.hasWritePermission;
        this.commentModalData.parcelID = this.parcelID;
        this.commentModalData.parcelAcctNum = this.parcelAcctNum;
        this.commentModalData.description = description;
        this.commentModalData.year = this.currentYear.annualYear1.toString();

        // attachment data
        this.attachmentModalData = new AttachmentModalData();
        this.attachmentModalData.belowParcelEntity = new AttachmentModalEntityData();
        this.attachmentModalData.belowParcelEntity.id = assessment.annualAssessmentID;
        this.attachmentModalData.belowParcelEntity.typeId = ASSESSMENT_ENTITY_TYPE_ID;
        this.attachmentModalData.belowParcelEntity.name = assessment.revisionDesc;
        this.attachmentModalData.entityType = 'Assessment';
        this.attachmentModalData.parentId = this.parcelID;
        this.attachmentModalData.parentType = 'Parcel';
        this.attachmentModalData.entityName = this.parcelAcctNum;
        this.attachmentModalData.entityDescription = description;
        this.attachmentModalData.year = this.currentYear.annualYear1;
    }

    recalculateVariables() {
        if (this.currentYear) {
            // "First" in array order is the leftmost on the grid, so it's actually the last one chronologically
            const lastAssessment = _.head(this.currentYear.annualGridDetails);
            this.isLastAssessment = lastAssessment.annualAssessmentID === this.model.annualAssessmentID;
        }

        this.model.annualAssessmentDetails = _.map(this.model.annualAssessmentDetails, (component) => {
            component.displayRatio = new Decimal(component.ratio).times(100).toDecimalPlaces(3).toNumber();
            return component;
        });
    }
}

//This is for retrieving all data for the assessment when we're loading it outside the grid
export class AssessmentViewDTO {
    annualYearView: AnnualDetailYear;
    assessment: Assessment;
    assessorID: number;
    componentTypes: any[];
}
